import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Lock,
  Bot,
  MessageSquare,
  Globe,
  BarChart3,
  Webhook,
  Package,
  Zap,
  AlertTriangle,
  Workflow,
  ChevronRight,
  Menu,
  X,
  Copy,
  Check,
  Terminal,
  Code2,
  Play,
  Home,
  BookOpen,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import { apiSections, sidebarNav } from "../data/apiDocs.js";
import CodeBlock from "../components/CodeBlock";
import ApiPlayground from "../components/ApiPlayground";
import IntegrationGuide from "../components/IntegrationGuide";
import SEOHead from "../components/SEOHead";

const iconMap = {
  Lock,
  Bot,
  MessageSquare,
  Globe,
  BarChart3,
  Webhook,
  Package,
  Zap,
  AlertTriangle,
  Workflow,
};

const methodColors = {
  GET: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  POST: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PUT: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
  PATCH: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

function TabCodeExamples({ endpoint, t }) {
  const [activeTab, setActiveTab] = useState("curl");
  const tabs = [
    { id: "curl", label: t("api_docs.curl"), icon: Terminal },
    { id: "javascript", label: t("api_docs.javascript"), icon: Code2 },
    { id: "python", label: t("api_docs.python"), icon: Code2 },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-1 overflow-x-auto border-b border-zinc-800 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs rounded transition-colors ${
              activeTab === tab.id
                ? "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
          >
            <tab.icon size={12} />
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === "curl" && endpoint.curl && (
        <CodeBlock code={endpoint.curl} language="bash" />
      )}
      {activeTab === "javascript" && endpoint.javascript && (
        <CodeBlock code={endpoint.javascript} language="javascript" />
      )}
      {activeTab === "python" && endpoint.python && (
        <CodeBlock code={endpoint.python} language="python" />
      )}
    </div>
  );
}

function ParamTable({ parameters, t }) {
  if (!parameters || parameters.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.parameter")}</th>
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.type")}</th>
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.location")}</th>
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.required")}</th>
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.description")}</th>
          </tr>
        </thead>
        <tbody>
          {parameters.map((param, idx) => (
            <tr key={idx} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
              <td className="py-2 px-3 text-amber-400">{param.name}</td>
              <td className="py-2 px-3 text-zinc-400">{param.type}</td>
              <td className="py-2 px-3">
                <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[10px]">
                  {param.location}
                </span>
              </td>
              <td className="py-2 px-3">
                {param.required ? (
                  <span className="text-red-400">{t("api_docs.required")}</span>
                ) : (
                  <span className="text-zinc-600">{t("api_docs.optional")}</span>
                )}
              </td>
              <td className="py-2 px-3 text-zinc-400">{param.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusCodeTable({ statusCodes, t }) {
  if (!statusCodes || statusCodes.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.code")}</th>
            <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.description")}</th>
          </tr>
        </thead>
        <tbody>
          {statusCodes.map((sc, idx) => (
            <tr key={idx} className="border-b border-zinc-800/50">
              <td className="py-2 px-3">
                <span
                  className={`px-2 py-0.5 rounded text-[11px] ${
                    sc.code >= 200 && sc.code < 300
                      ? "bg-emerald-500/20 text-emerald-400"
                      : sc.code >= 400 && sc.code < 500
                      ? "bg-amber-500/20 text-amber-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {sc.code}
                </span>
              </td>
              <td className="py-2 px-3 text-zinc-400">{sc.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EndpointBlock({ endpoint, t }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-4">
      <div
        className="flex items-start gap-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <span
          className={`shrink-0 px-2 py-1 font-mono text-[9px] sm:text-[11px] font-bold uppercase rounded border ${
            methodColors[endpoint.method] || "bg-zinc-700 text-zinc-300"
          }`}
        >
          {endpoint.method}
        </span>
        <div className="flex-1 min-w-0">
          <code className="text-xs sm:text-sm font-mono text-zinc-200 break-all">
            {endpoint.path}
          </code>
          <p className="mt-1 text-xs text-zinc-400">{endpoint.description}</p>
        </div>
        <button className="shrink-0 mt-1 text-zinc-500 hover:text-amber-500 transition-colors">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 pl-0 sm:pl-4 border-l-2 border-zinc-800 ml-1">
          <ParamTable parameters={endpoint.parameters} t={t} />

          <div className="space-y-4">
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                {t("api_docs.request_example")}
              </h4>
              <TabCodeExamples endpoint={endpoint} t={t} />
            </div>

            {endpoint.response && (
              <div>
                <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {t("api_docs.response")}
                </h4>
                <CodeBlock code={endpoint.response} language="json" />
              </div>
            )}

            <StatusCodeTable statusCodes={endpoint.statusCodes} t={t} />
          </div>
        </div>
      )}
    </div>
  );
}

function AuthSection({ section, t }) {
  const content = section.content;
  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">{content.description}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {content.methods?.map((method, idx) => (
          <div
            key={idx}
            className="flex items-start gap-3 p-4 rounded-lg border border-zinc-800 bg-zinc-950/50"
          >
            <Lock size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-zinc-200">
                  {method.name}
                </span>
                <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-zinc-800 text-zinc-400">
                  {method.location}
                </span>
              </div>
              <p className="mt-1 text-xs text-zinc-500">{method.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
          {t("api_docs.authentication.usage_examples")}
        </h3>
        <TabCodeExamples
          endpoint={{
            curl: content.curl,
            javascript: content.javascript,
            python: content.python,
          }}
          t={t}
        />
      </div>

      {content.response && (
        <div>
          <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {t("api_docs.response")}
          </h4>
          <CodeBlock code={content.response} language="json" />
        </div>
      )}
    </div>
  );
}

function WebhooksSection({ section, t }) {
  const content = section.content;
  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">{content.description}</p>

      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
          {t("api_docs.webhooks.create_webhook")}
        </h3>
        {content.configuration && (
          <TabCodeExamples
            endpoint={{
              curl: content.configuration.curl,
              javascript: content.configuration.javascript,
              python: content.configuration.python,
            }}
            t={t}
          />
        )}
      </div>

      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
          {t("api_docs.webhooks.available_events")}
        </h3>
        <div className="space-y-4">
          {content.events?.map((event, idx) => (
            <div
              key={idx}
              className="p-4 rounded-lg border border-zinc-800 bg-zinc-950/50"
            >
              <div className="flex items-center gap-2 mb-2">
                <Webhook size={14} className="text-amber-500" />
                <code className="font-mono text-sm text-zinc-200">{event.name}</code>
              </div>
              <p className="text-xs text-zinc-500 mb-3">{event.description}</p>
              <div>
                <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {t("api_docs.webhooks.payload_example")}
                </h4>
                <CodeBlock code={event.payload} language="json" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SdksSection({ section, t }) {
  const content = section.content;
  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">{content.description}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {content.sdks?.map((sdk, idx) => (
          <div
            key={idx}
            className="p-5 rounded-lg border border-zinc-800 bg-zinc-950/50 space-y-4"
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-amber-500" />
              <div>
                <h3 className="font-mono text-sm font-bold text-zinc-200">
                  {sdk.name}
                </h3>
                <code className="text-[11px] text-zinc-500">{sdk.package}</code>
              </div>
            </div>

            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {t("api_docs.sdks.install")}
              </span>
              <CodeBlock code={sdk.install} language="bash" />
            </div>

            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {t("api_docs.sdks.quick_start")}
              </span>
              <CodeBlock code={sdk.example} language="javascript" />
            </div>

            <a
              href={sdk.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-mono text-amber-500 hover:text-amber-400 transition-colors"
            >
              {t("api_docs.sdks.view_on_github")}
              <ChevronRight size={12} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function RateLimitingSection({ section, t }) {
  const content = section.content;
  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">{content.description}</p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.rate_limiting.plan")}</th>
              <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.rate_limiting.rate_limit")}</th>
              <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.rate_limiting.burst")}</th>
            </tr>
          </thead>
          <tbody>
            {content.limits?.map((limit, idx) => (
              <tr key={idx} className="border-b border-zinc-800/50">
                <td className="py-2 px-3 text-amber-400 font-bold">{limit.plan}</td>
                <td className="py-2 px-3 text-zinc-300">{limit.requests}</td>
                <td className="py-2 px-3 text-zinc-300">{limit.burst}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
          {t("api_docs.rate_limiting.rate_limit_headers")}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t('api_docs.header')}</th>
                <th className="text-left py-2 px-3 text-zinc-500 uppercase tracking-wider">{t("api_docs.description")}</th>
              </tr>
            </thead>
            <tbody>
              {content.headers?.map((header, idx) => (
                <tr key={idx} className="border-b border-zinc-800/50">
                  <td className="py-2 px-3 text-amber-400">{header.name}</td>
                  <td className="py-2 px-3 text-zinc-400">{header.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {content.curl && (
        <div>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
            {t("api_docs.rate_limiting.example")}
          </h3>
          <CodeBlock code={content.curl} language="bash" />
        </div>
      )}
    </div>
  );
}

function ErrorCodesSection({ section, t }) {
  const content = section.content;
  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">{content.description}</p>

      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
          {t("api_docs.error_codes.error_response_format")}
        </h3>
        <CodeBlock code={content.errorFormat} language="json" />
      </div>

      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">
          {t("api_docs.status_codes")}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-2 px-2 sm:py-3 sm:px-4 text-zinc-500 uppercase tracking-wider">{t("api_docs.code")}</th>
                <th className="text-left py-2 px-2 sm:py-3 sm:px-4 text-zinc-500 uppercase tracking-wider">{t("api_docs.error_codes.name")}</th>
                <th className="text-left py-2 px-2 sm:py-3 sm:px-4 text-zinc-500 uppercase tracking-wider">{t("api_docs.description")}</th>
              </tr>
            </thead>
            <tbody>
              {content.codes?.map((code, idx) => (
                <tr
                  key={idx}
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/50"
                >
                  <td className="py-3 px-4">
                    <span
                      className={`inline-block px-2 py-0.5 rounded font-bold text-[11px] ${
                        code.code >= 200 && code.code < 300
                          ? "bg-emerald-500/20 text-emerald-400"
                          : code.code >= 400 && code.code < 500
                          ? "bg-amber-500/20 text-amber-400"
                          : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {code.code}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-zinc-200 font-bold">{code.name}</td>
                  <td className="py-3 px-4 text-zinc-400">{code.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SectionContent({ section, t }) {
  if (section.id === "authentication") {
    return <AuthSection section={section} t={t} />;
  }
  if (section.id === "webhooks") {
    return <WebhooksSection section={section} t={t} />;
  }
  if (section.id === "sdks") {
    return <SdksSection section={section} t={t} />;
  }
  if (section.id === "rate-limiting") {
    return <RateLimitingSection section={section} t={t} />;
  }
  if (section.id === "error-codes") {
    return <ErrorCodesSection section={section} t={t} />;
  }

  // API sections with endpoints
  if (section.endpoints) {
    return (
      <div className="space-y-4">
        {section.endpoints.map((endpoint, idx) => (
          <div
            key={idx}
            className="p-3 sm:p-6 rounded-lg border border-zinc-800 bg-zinc-950/30 space-y-4"
          >
            <EndpointBlock endpoint={endpoint} t={t} />
          </div>
        ))}
      </div>
    );
  }

  return null;
}

export default function ApiDocs() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("authentication");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const contentRef = useRef(null);

  // Build searchable index of all endpoints
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const results = [];
    apiSections.forEach((section) => {
      if (section.endpoints) {
        section.endpoints.forEach((ep) => {
          const matchStr = `${ep.method} ${ep.path} ${ep.description}`.toLowerCase();
          if (matchStr.includes(q)) {
            results.push({
              sectionId: section.id,
              sectionTitle: section.title,
              endpoint: ep,
            });
          }
        });
      }
    });
    return results;
  }, [searchQuery]);

  const scrollToSection = useCallback((id) => {
    setActiveSection(id);
    const el = document.getElementById(`section-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileMenuOpen(false);
    setSearchQuery("");
  }, []);

  // Intersection Observer for scrollspy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace("section-", "");
            setActiveSection(id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );

    apiSections.forEach((s) => {
      const el = document.getElementById(`section-${s.id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const breadcrumbItems = [
    { label: t("nav.home"), path: "/" },
    { label: "Documentation", path: "#" },
    { label: t("api_docs.page_title") },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <SEOHead
        title={t('api_docs.api_documentation_max_employee_developer_docs')}
        description="Complete API reference for Max OS Cloud. Endpoints, authentication, webhooks, and integration guides for building with AI digital workers."
        url="/docs/api"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
              { "@type": "ListItem", position: 2, name: "API Docs", item: "https://maxemployee.com/docs/api" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: "Max OS Cloud API Documentation",
            description: "Complete API reference for building with AI digital workers.",
            url: "https://maxemployee.com/docs/api",
          },
        ]}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-7 h-7 bg-amber-500 rounded flex items-center justify-center">
                  <Zap size={16} className="text-black" />
                </div>
                <span className="font-mono text-sm font-bold tracking-wider text-zinc-200 hidden sm:inline">{t('api_docs.max_os_cloud')}</span>
              </Link>
              <span className="text-zinc-700">|</span>
              <span className="font-mono text-xs text-zinc-500">{t("api_docs.page_title")}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {t("api_docs.version")}
              </span>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-zinc-400 hover:text-zinc-200 transition-colors"
                aria-label={t('api_docs.toggle_menu')}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <Breadcrumbs items={breadcrumbItems} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <aside
            className={`${
              mobileMenuOpen ? "fixed inset-0 z-40 bg-zinc-950 pt-14 overflow-y-auto" : "hidden"
            } lg:block lg:static lg:bg-transparent lg:pt-0`}
          >
            <nav className="sticky top-20 w-64 shrink-0">
              <div className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-5rem)]">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 px-3 mb-3">
                  {t("api_docs.contents")}
                </div>

                {/* Search bar in sidebar */}
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("api_docs.search_placeholder")}
                    className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-xs font-mono text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20"
                  />
                </div>

                {/* Search results or nav items */}
                {searchResults ? (
                  <div className="space-y-1">
                    {searchResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-zinc-600 font-mono">
                        {t("api_docs.no_results")}
                      </div>
                    ) : (
                      searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => scrollToSection(result.sectionId)}
                          className="w-full text-left px-3 py-2 rounded-md text-xs font-mono hover:bg-zinc-900/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`shrink-0 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase rounded border ${
                              methodColors[result.endpoint.method] || "bg-zinc-700 text-zinc-300"
                            }`}>
                              {result.endpoint.method}
                            </span>
                            <span className="text-zinc-400 truncate">{result.endpoint.path}</span>
                          </div>
                          <div className="text-[10px] text-zinc-600 mt-0.5 truncate">
                            {result.sectionTitle} — {result.endpoint.description}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  sidebarNav.map((item) => {
                    const section = apiSections.find((s) => s.id === item.id);
                    const Icon = section?.icon ? iconMap[section.icon] : null;
                    return (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm font-mono transition-colors ${
                          activeSection === item.id
                            ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500"
                            : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/50 border-l-2 border-transparent"
                        }`}
                      >
                        {Icon && <Icon size={14} />}
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main ref={contentRef} id="main-content" className="flex-1 min-w-0">
            {/* Hero */}
            <div className="mb-6 sm:mb-10 pb-8 border-b border-zinc-800">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-mono font-bold text-zinc-100 mb-3">
                {t("api_docs.page_title")}
              </h1>
              <p className="text-zinc-400 max-w-2xl">
                {t("api_docs.page_subtitle")}
              </p>
              <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-xs font-mono text-zinc-400">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {t("api_docs.base_url")}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-xs font-mono text-zinc-400">
                  {t("api_docs.protocol")}
                </span>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-12">
              {apiSections.map((section) => {
                const Icon = section.icon ? iconMap[section.icon] : null;
                return (
                  <section
                    key={section.id}
                    id={`section-${section.id}`}
                    className="scroll-mt-24"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      {Icon && (
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                          <Icon size={20} className="text-amber-500" />
                        </div>
                      )}
                      <h2 className="text-xl sm:text-2xl font-mono font-bold text-zinc-100">
                        {section.title}
                      </h2>
                    </div>
                    <SectionContent section={section} t={t} />
                  </section>
                );
              })}
            </div>

            {/* Integration Guides */}
            <section className="mt-8 sm:mt-16 scroll-mt-24" id="section-guides">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <BookOpen size={20} className="text-amber-500" />
                </div>
                <h2 className="text-2xl font-mono font-bold text-zinc-100">
                  {t("api_docs.guides.title")}
                </h2>
              </div>
              <IntegrationGuide />
            </section>

            {/* Interactive Playground */}
            <section className="mt-12 p-3 sm:p-6 rounded-lg border border-zinc-800 bg-zinc-950/30 space-y-4 scroll-mt-24" id="section-playground">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <Play size={20} className="text-amber-500" />
                </div>
                <div>
                  <h2 className="text-xl font-mono font-bold text-zinc-100">
                    {t("api_docs.playground.title")}
                  </h2>
                  <p className="text-xs text-zinc-500">
                    {t("api_docs.playground.subtitle")}
                  </p>
                </div>
              </div>

              <ApiPlayground />
            </section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-xs font-mono text-zinc-600">
              <span>Max OS Cloud API {t("api_docs.version")}</span>
              <span>|</span>
              <span>{t("api_docs.last_updated")}</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-xs font-mono text-zinc-600 hover:text-amber-500 transition-colors"
              >
                {t("api_docs.back_to_home")}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
